'use client'

import { useEffect } from 'react'
import FocusLock from 'react-focus-lock'
import { RemoveScroll } from 'react-remove-scroll'

import { Text } from '@/components/Text/Text'

import css from './ImageLightBox.module.css'

export type ImageLightBoxProps = {
    src?: string
    webp?: string
    captionText?: string
    onClose: () => void
}

/** A global ImageLightBox */
export function ImageLightBox({ src, webp, captionText, onClose }: ImageLightBoxProps) {
    useEffect(() => {
        function handleKeyDown(event: KeyboardEvent) {
            if (event.code === 'Escape') {
                onClose()
            }
        }

        window.addEventListener('keydown', handleKeyDown)

        return () => {
            window.removeEventListener('keydown', handleKeyDown)
        }
    }, [onClose])

    return (
        <FocusLock returnFocus>
            <RemoveScroll>
                <div
                    className={css.imageLightBox}
                    onClick={onClose}
                    onKeyDown={onClose}
                    aria-label="Close"
                    // Focus lock requires something be focusable. ImageLightBox doesn't
                    // have any focusable elements so we set tabIndex to -1 to make it programmaticaly focusable.
                    // TODO: Add a close button that should receive focus
                    tabIndex={-1}
                >
                    <div className={css.backgroundOverlay} />
                    <div className={css.container}>
                        <figure>
                            <picture>
                                {webp && <source srcSet={webp} type="image/webp" />}
                                {src && <img src={src} alt={captionText} />}
                            </picture>
                            {captionText && <Text tag="figcaption">{captionText}</Text>}
                        </figure>
                    </div>
                </div>
            </RemoveScroll>
        </FocusLock>
    )
}
